import React from 'react';
import { useEmailAuth } from '../services/emailAuth';
import { fetchEmails, markEmailAsProcessed, storeEmailInFirebase, extractDateFromEmail } from '../services/emailService';
import { processEmailAttachments } from '../services/emailAttachmentProcessor';
import { processTLDRForSeesawEmail } from '../services/emailDateExtractor';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './GoogleCalendar.css'; // Reuse the same styling
import { trackEmail, trackFeature, trackError } from '../utils/analytics';

/* EMAIL BUTTON COMPONENT - Gmail integration button with auto-import */
/* Used on Home page to connect Gmail and import event emails */
/* Shows connection status, user info, and import progress */
const EmailButton = ({ onEventsExtracted }) => {
  const { isAuthenticated, signIn, signOut, isLoading, user, refreshToken } = useEmailAuth();
  const { currentUser } = useAuth();
  const [isImporting, setIsImporting] = React.useState(false);
  const [hasAutoImported, setHasAutoImported] = React.useState(false);
  
  /* DISABLED: AUTO-IMPORT FUNCTIONALITY - Automatically imports emails on authentication */
  /* DISABLED: Sets up 5-minute periodic checks for new emails when connected */
  // React.useEffect(() => {
  //   // Initial auto-import when authenticated
  //   const autoImportEmails = async () => {
  //     // Check if user is authenticated and we're not currently importing
  //     if (isAuthenticated && !isImporting) {
  //       // Check if token exists in localStorage
  //       const token = localStorage.getItem('emailAccessToken');
  //       if (token) {
  //         console.log('Auto importing emails...');
  //         // Add a delay to ensure token is properly saved
  //         setTimeout(async () => {
  //           await importEmails();
  //           setHasAutoImported(true);
  //         }, 1000);
  //       } else {
  //         console.log('Email token not found in localStorage, skipping auto-import');
  //         console.log('Authentication state:', isAuthenticated);
  //         console.log('Available tokens:', 
  //                    'emailAccessToken=', localStorage.getItem('emailAccessToken'),
  //                    'googleAccessToken=', localStorage.getItem('googleAccessToken'));
  //       }
  //     }
  //   };
  //   
  //   // Run initial auto-import
  //   autoImportEmails();
  //   
  //   // Set up interval for periodic imports (every 5 minutes)
  //   let intervalId = null;
  //   if (isAuthenticated) {
  //     intervalId = setInterval(async () => {
  //       if (!isImporting) { // Prevent overlapping imports
  //         // Check if token exists before attempting import
  //         const token = localStorage.getItem('emailAccessToken');
  //         if (token) {
  //           console.log('Periodic check for new emails...');
  //           await importEmails();
  //         }
  //       }
  //     }, 300000); // 300000 ms = 5 minutes
  //   }
  //   
  //   // Clean up interval when component unmounts or auth state changes
  //   return () => {
  //     if (intervalId) {
  //       clearInterval(intervalId);
  //     }
  //   };
  // }, [isAuthenticated]);
  
  const handleImportEmails = async () => {
    if (!isAuthenticated) {
      // Track Gmail connection attempt
      trackFeature.settings('gmail_connect_attempt');
      
      // Check if we should use backend auth
      const useBackendAuth = localStorage.getItem('preferBackendAuth') === 'true';
      
      if (useBackendAuth) {
        try {
          console.log('🔄 Using backend authentication for Gmail...');
          const functions = getFunctions();
          const initAuth = httpsCallable(functions, 'initGoogleAuth');
          const result = await initAuth();
          window.location.href = result.data.authUrl;
        } catch (error) {
          console.error('Backend auth failed, falling back to frontend:', error);
          trackError('gmail_backend_auth_failed', error.message);
          signIn();
        }
      } else {
        signIn();
      }
      return;
    }
    
    // Check if token exists in localStorage - extra safety measure
    const token = localStorage.getItem('emailAccessToken');
    if (!token) {
      console.log('Token missing despite authenticated state, forcing sign-in');
      signIn();
      return;
    }
    
    // Reset auto-import flag when manually importing
    setHasAutoImported(false);
    importEmails();
  };
  
  // Import emails and extract events
  const importEmails = async () => {
    const importStartTime = Date.now();
    try {
      setIsImporting(true);
      
      // Get token from storage
      const token = localStorage.getItem('emailAccessToken');
      if (!token) {
        console.log('No Gmail access token found, need to sign in first');
        // We can't directly set authentication state, just log it
        // The user will need to click the button to sign in
        setIsImporting(false);
        return;
      }
      
      try {
        // Fetch emails from Gmail API
        const emails = await fetchEmails(token, 50, false); // Only get unprocessed emails, max 50
        
        // Track email view event
        trackEmail.view(emails.length);
        
        if (emails.length === 0) {
          console.log('No new emails found.');
          setIsImporting(false);
          return;
        }
        
        console.log(`Processing ${emails.length} emails...`);
        
        // Process each email to extract events
        const results = await Promise.all(
          emails.map(async (email) => {
            console.log(`Processing email: ${email.subject}`);
            
            // Track email open
            const emailType = email.from?.includes('seesaw') ? 'seesaw' : 
                           email.from?.includes('arbor') ? 'arbor' : 'other';
            trackEmail.open(emailType, false);
            
            // Extract dates from email body using OpenAI
            const bodyEvents = await extractDateFromEmail(email.body, email.subject, config.OPENAI_API_KEY, email.from);
            console.log(`Extracted ${bodyEvents.length} events from email body`);
            
            // Process attachments if any
            let attachmentEvents = [];
            try {
              console.log(`Checking for attachments in email: ${email.subject}`);
              attachmentEvents = await processEmailAttachments(token, email.id, email.subject, email.from);
              console.log(`Extracted ${attachmentEvents.length} events from email attachments`);
              if (attachmentEvents.length > 0) {
                trackEmail.open(emailType, true); // Track again with attachment flag
              }
            } catch (attachmentError) {
              console.error(`Error processing attachments for email ${email.id}:`, attachmentError);
              trackError('email_attachment_processing', attachmentError.message);
            }
            
            // Combine events from body and attachments
            const allEvents = [...bodyEvents, ...attachmentEvents];
            console.log(`Total events extracted: ${allEvents.length}`);
            
            // Process TLDR for Seesaw emails (both direct and forwarded)
            let tldr = null;
            
            // Check if it's a direct Seesaw email
            const isDirectSeesawEmail = email.from && email.from.includes('notifications.seesaw.me');
            
            // Check if it's a forwarded Seesaw email by looking in the body
            const isForwardedSeesawEmail = email.body && (
              email.body.includes('notifications.seesaw.me') || 
              email.body.includes('From: notifications@notifications.seesaw.me') ||
              email.body.includes('From: noreply@notifications.seesaw.me') ||
              email.body.includes('From: do-not-reply@notifications.seesaw.me')
            );
            
            // Store the email and all events in Firebase with user ID FIRST
            await storeEmailInFirebase(email, allEvents, currentUser?.uid);
            
            if (isDirectSeesawEmail || isForwardedSeesawEmail) {
              const emailType = isDirectSeesawEmail ? 'direct' : 'forwarded';
              console.log(`Processing TLDR for ${emailType} Seesaw email: ${email.subject}`);
              tldr = await processTLDRForSeesawEmail(email, config.OPENAI_API_KEY, currentUser?.uid);
              if (tldr) {
                console.log(`TLDR extracted: ${tldr.summary}`);
              }
            }
            
            // Mark the email as processed in Gmail
            await markEmailAsProcessed(token, email.id);
            
            return {
              emailId: email.id,
              subject: email.subject,
              events: allEvents,
              tldr: tldr
            };
          })
        );
        
        // Collect all extracted events and TLDRs
        const allExtractedEvents = results.flatMap(result => result.events);
        const extractedTLDRs = results.filter(result => result.tldr).map(result => result.tldr);
        
        // Track email processing completion
        const processingTime = Date.now() - importStartTime;
        trackEmail.process('batch', processingTime);
        
        // Track feature usage for event extraction
        if (allExtractedEvents.length > 0) {
          trackFeature.search('email_events', allExtractedEvents.length);
        }
        
        // Show success message with count
        if (allExtractedEvents.length > 0 || extractedTLDRs.length > 0) {
          console.log(`Extracted ${allExtractedEvents.length} events and ${extractedTLDRs.length} TLDRs from ${emails.length} emails`);
          
          // Pass the converted events back to the parent component
          if (onEventsExtracted) {
            onEventsExtracted(allExtractedEvents);
          }
          
          // Only show alert if user manually triggered the import
          if (!hasAutoImported) {
            const message = `Successfully processed ${emails.length} emails:\n` +
              `- ${allExtractedEvents.length} events extracted\n` +
              `- ${extractedTLDRs.length} TLDRs created`;
            alert(message);
          }
        } else {
          // Only show alert if user manually triggered the import
          if (!hasAutoImported) {
            alert(`Processed ${emails.length} emails but no events or TLDRs were found.`);
          }
        }
      } catch (apiError) {
        console.error('API Error:', apiError);
        
        // Check for specific errors
        if (apiError.message === 'TOKEN_EXPIRED') {
          console.log('Token expired, attempting to refresh...');
          
          try {
            // Get current tokens before refreshing
            const currentToken = localStorage.getItem('emailAccessToken');
            const refreshTokenValue = localStorage.getItem('emailRefreshToken');
            
            console.log('Current token state:', 
                       'Access token exists:', !!currentToken, 
                       'Refresh token exists:', !!refreshTokenValue);
            
            // Clear any validation cache to force re-check with Google
            localStorage.removeItem('emailTokenLastValidated');
            
            // First try to refresh the token silently
            const newToken = await refreshToken();
            
            if (newToken) {
              console.log('Successfully refreshed token, retrying import...');
              // Retry the import with the new token
              await importEmails();
              return;
            } else {
              console.log('Failed to refresh token silently, checking token status...');
              
              // Attempt to validate the current token directly with Google
              try {
                const response = await fetch(`https://www.googleapis.com/oauth2/v3/tokeninfo?access_token=${currentToken}`);
                const isStillValid = response.ok;
                
                if (isStillValid) {
                  console.log('Token is actually still valid according to Google, continuing...');
                  // Update local validation timestamp
                  localStorage.setItem('emailTokenLastValidated', new Date().getTime().toString());
                  // Retry import
                  await importEmails();
                  return;
                }
              } catch (validationError) {
                console.error('Error validating token with Google:', validationError);
              }
              
              // If we get here, we need to sign in again
              console.log('Token validation failed, will try normal sign in');
              // If silent refresh fails but we're auto-importing, just retry sign in without alerts
              if (!hasAutoImported) {
                alert('Your Gmail session needs to be refreshed. Reconnecting automatically...');
              }
              
              // Try to sign in again (with minimal UI)
              signIn();
              return;
            }
          } catch (refreshError) {
            console.error('Error during token refresh:', refreshError);
            // If refresh completely fails and this was user-initiated, show a friendly message
            if (!hasAutoImported) {
              alert('Unable to refresh your Gmail session. Please try connecting again.');
            }
            return;
          }
        }
        else if (apiError.message && apiError.message.includes('insufficient authentication scopes')) {
          // For scope issues we do need to sign out and re-authenticate with full consent
          if (!hasAutoImported) {
            alert('You need additional permissions to access your emails. Please reconnect your account.');
          }
          
          // Sign out and then prompt for sign in with full consent
          signOut();
          setTimeout(() => {
            signIn(); // Start sign-in again with full consent
          }, 1000);
          return;
        }
        
        // Rethrow to be caught by outer catch
        throw apiError;
      }
    } catch (error) {
      console.error('Error importing emails:', error);
      // Only show alert if user manually triggered the import
      if (!hasAutoImported) {
        alert(`Failed to import emails: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsImporting(false);
    }
  };
  
  if (isLoading) {
    return (
      <button 
        className="google-calendar-button loading"
        disabled
        style={buttonStyle}
      >
        <div className="spinner" style={spinnerStyle}></div>
        Loading...
      </button>
    );
  }
  
  /* GMAIL BUTTON UI - Main button with connected/disconnected states */
  /* Shows: Connect Gmail → Import Emails → Import Again based on state */
  return (
    <div className="google-calendar-container" style={containerStyle}>
      <button 
        className={`google-calendar-button ${isAuthenticated ? 'connected' : ''}`}
        onClick={handleImportEmails}
        disabled={isImporting}
        style={{
          ...buttonStyle,
          backgroundColor: isAuthenticated ? '#DB4437' : '#ffffff', // Gmail red color
          color: isAuthenticated ? '#ffffff' : '#444444',
        }}
      >
        {isImporting ? (
          <>
            <div className="spinner" style={spinnerStyle}></div>
            Importing Emails...
          </>
        ) : (
          <>
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg" 
              alt="Gmail" 
              style={iconStyle} 
            />
            {isAuthenticated 
              ? 'Import Emails'
              : 'Connect Gmail'}
          </>
        )}
      </button>
      
      {/* AUTHENTICATED USER SECTION - Shows when Gmail is connected */}
      {/* Displays user profile pic, name, and disconnect option */}
      {isAuthenticated && (
        <>
          <div className="google-auth-info" style={userInfoStyle}>
            <div style={userDetail}>
              {user?.picture && (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  style={userImageStyle} 
                />
              )}
              <span style={userNameStyle}>{user?.name || 'Gmail User'}</span>
            </div>
            <button 
              onClick={signOut} 
              style={signOutButtonStyle}
            >
              Disconnect
            </button>
          </div>
          
        </>
      )}
    </div>
  );
};

/* COMPONENT STYLES - Layout and appearance for Gmail button and user info */
/* Reuses GoogleCalendarButton styles with Gmail-specific modifications */
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  margin: '15px 0',
  width: '100%',
};

const buttonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 16px',
  borderRadius: '8px',
  border: '1px solid #dadce0',
  fontSize: '15px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  height: '44px',
  width: '100%',
  maxWidth: '280px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const iconStyle = {
  width: '20px',
  height: '20px',
  marginRight: '10px',
};

const spinnerStyle = {
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  border: '2px solid #f3f3f3',
  borderTop: '2px solid #DB4437', // Gmail red
  animation: 'spin 1s linear infinite',
  marginRight: '10px',
};

const userInfoStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: '280px',
  padding: '8px 0',
  marginTop: '8px',
};

const userDetail = {
  display: 'flex',
  alignItems: 'center',
};

const userImageStyle = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  marginRight: '8px',
};

const userNameStyle = {
  fontSize: '14px',
  color: '#444444',
};

const signOutButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#DB4437', // Gmail red
  fontSize: '14px',
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: '4px',
};

export default EmailButton;